import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const when = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export const InquiryNotes = ({ item, setItems }) => {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const notes = Array.isArray(item.notes) ? item.notes : [];
  const patch = (fn) => setItems((xs) => xs.map((x) => (x.id === item.id ? { ...x, notes: fn(Array.isArray(x.notes) ? x.notes : []) } : x)));

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/inquiries/${item.id}/notes`, { text });
      patch((ns) => [...ns, data]);
      setText("");
      toast.success("Note saved");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/admin/inquiries/${item.id}/notes/${id}`);
      patch((ns) => ns.filter((n) => n.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="my-4 rounded-2xl border border-[#2E2A24] bg-[#0A0A0C] p-4" data-testid="inquiry-notes">
      <p className="font-mono-rc text-[11px] uppercase tracking-[0.18em] text-zinc-400">Private notes · only your team sees these</p>
      <ul className="mt-3 space-y-3">
        {notes.map((n) => (
          <li key={n.id} className="rounded-xl bg-[#121215] p-3" data-testid={`inquiry-note-${n.id}`}>
            <div className="flex items-start justify-between gap-3">
              <p className="whitespace-pre-wrap text-sm text-[#F5EFE6]">{n.text}</p>
              {(user?.role === "admin" || user?.email === n.author) && (
                <button onClick={() => remove(n.id)} aria-label="Delete note" className="shrink-0 text-zinc-400 hover:text-red-300" data-testid={`inquiry-note-delete-${n.id}`}><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
            <p className="mt-2 text-xs text-zinc-400">{n.role === "booking" ? "Booking" : "Admin"} · {when(n.created_at)}</p>
          </li>
        ))}
      </ul>
      {!notes.length && <p className="mt-2 text-sm text-zinc-400">No notes yet. Log calls, emails and next steps here.</p>}
      <form onSubmit={add} className="mt-3 flex flex-col gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={4000} placeholder="e.g. Called Pastor James — sending proposal Friday" className="w-full rounded-xl border border-[#2E2A24] bg-[#121215] px-3 py-2 text-sm text-[#F5EFE6] placeholder:text-zinc-500 focus:border-[#C4832B] focus:outline-none" data-testid="inquiry-note-input" />
        <button type="submit" disabled={busy || !text.trim()} className="self-end rounded-full bg-[#C4832B] px-5 py-2 text-sm font-medium text-[#0A0A0C] transition-opacity disabled:opacity-40" data-testid="inquiry-note-save-btn">Add note</button>
      </form>
    </div>
  );
};
